import { uniq, uniqBy } from "lodash";
import { CalendarEvent, EventUpdate } from "../models";
import { logger } from "../utils";
import { getCADchatSpaceLink } from "../utils/helpers";
import {
  addOrReplaceCADchatPendingMeetingBlock,
  CADCHAT_STAGE_CUSTOM_PROPERTY,
  CADchatMeetingStagePayload,
  encodeCADchatMeetingStagePayload,
  hasCADchatMeetingStagePayload,
  removeCADchatPendingMeetingBlock,
} from "../utils/cadchatMeetingStaging";

export class OfficeService {
  private _isReady: boolean = false;
  private _restId: string | undefined;

  constructor() {
    // Initialize Office.js
    if (typeof Office === "undefined") {
      throw new Error("Office.js is not loaded");
    }

    // Check if Office is ready
    Office.onReady(() => {
      this._isReady = true;
    });
  }

  get isReady() {
    return this._isReady;
  }

  get mailbox() {
    if (!this._isReady) return undefined;

    return Office?.context?.mailbox ? Office?.context?.mailbox : undefined;
  }

  get user(): Office.UserProfile | undefined {
    if (!this._isReady) return undefined;

    return this.mailbox ? this.mailbox?.userProfile : undefined;
  }

  get item() {
    if (!this._isReady) return undefined;

    return this.mailbox ? this.mailbox?.item : undefined;
  }

  public async itemId(): Promise<string | null> {
    return new Promise((resolve, reject) => {
      if (!this._isReady || !this.item) {
        reject("Item not available");
        return;
      }

      this.item.getItemIdAsync((result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          resolve(result.value);
        } else {
          //logger.error("Error getting item id", result.error.message);
          resolve(null);
        }
      });
    });
  }

  public itemType(): string {
    return this.item?.itemType || "";
  }

  public getRestId(id: string, restVersion: Office.MailboxEnums.RestVersion | string): string {
    if (!this.mailbox) return "";
    if (this._restId) return this._restId;

    const value = this.mailbox?.convertToRestId(id, restVersion);
    this._restId = value;
    return value;
  }

  //#region Subject

  public async subject(): Promise<string | null> {
    return new Promise((resolve, reject) => {
      if (!this.item) {
        reject("Item not available");
        return;
      }

      this.item.subject.getAsync((result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          resolve(result.value);
        } else {
          logger.error("Error getting subject", result.error.message);
          resolve(null);
        }
      });
    });
  }

  public async setSubject(subject: string): Promise<boolean> {
    return new Promise((resolve, reject) => {
      if (!this.item) {
        reject(false);
        return;
      }

      this.item.subject.setAsync(subject, (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          //result.asyncContext.completed({ allowEvent: true });
          resolve(true);
        } else {
          logger.error("Error saving subject", result.error.message);
          resolve(false);
        }
      });
    });
  }

  //#endregion

  //#region Date Times

  public async startDate(): Promise<Date | null> {
    return new Promise((resolve, reject) => {
      if (!this.item) {
        reject("Item not available");
        return;
      }

      this.item.start.getAsync((result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          resolve(result.value);
        } else {
          logger.error("Error getting start date", result.error.message);
          resolve(null);
        }
      });
    });
  }

  public async endDate(): Promise<Date | null> {
    return new Promise((resolve, reject) => {
      if (!this.item) {
        reject("Item not available");
        return;
      }

      this.item.end.getAsync((result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          resolve(result.value);
        } else {
          logger.error("Error getting end date", result.error.message);
          resolve(null);
        }
      });
    });
  }

  //#endregion

  //#region Location

  public async location(): Promise<string | null> {
    return new Promise((resolve, reject) => {
      if (!this.item) {
        reject("Item not available");
        return;
      }

      this.item.location.getAsync((result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          resolve(result.value);
        } else {
          logger.error("Error getting location", result.error.message);
          resolve(null);
        }
      });
    });
  }

  public async setLocation(location: string): Promise<boolean> {
    return new Promise((resolve, reject) => {
      if (!this.item) {
        reject(false);
        return;
      }

      this.item.location.setAsync(location, (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          resolve(true);
        } else {
          logger.error("Error saving location", result.error.message);
          resolve(false);
        }
      });
    });
  }

  //#endregion

  //#region Attendees

  private async _attendees(
    type: "required" | "optional"
  ): Promise<Office.EmailAddressDetails[] | null> {
    return new Promise((resolve, reject) => {
      if (!this.item) {
        logger.error("Item not available");
        reject("Item not available");
        return;
      }

      if (type !== "required" && type !== "optional") {
        reject("Invalid type. Use 'required' or 'optional'.");
        return;
      }

      if (type === "required") {
        this.item.requiredAttendees.getAsync((result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            //result.asyncContext.completed({ allowEvent: true });
            resolve(result.value);
          } else {
            logger.error("Error getting required attendees", result.error.message);
            resolve([]);
          }
        });
      } else if (type === "optional") {
        this.item.optionalAttendees.getAsync((result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            //result.asyncContext.completed({ allowEvent: true });
            resolve(result.value);
          } else {
            logger.error("Error getting optional attendees", result.error.message);
            resolve([]);
          }
        });
      }
    });
  }

  /**
   * Gets the list of attendees for the current item.
   *
   * @param {"required" | "optional" | "default"} type - The type of attendees to get.
   * @returns {Promise<Office.EmailAddressDetails[]>} - The list of attendees.
   */
  public async attendees(
    type: "required" | "optional" | "default" = "default"
  ): Promise<Office.EmailAddressDetails[]> {
    if (!this.item) throw new Error("Item not available");

    const requiredAttendees =
      type === "required" || type === "default" ? await this._attendees("required") : [];
    const optionalAttendees =
      type === "optional" || type === "default" ? await this._attendees("optional") : [];

    if (type === "default") {
      const allAttendees = requiredAttendees.concat(optionalAttendees);
      return uniqBy(
        allAttendees.map((attendee) => attendee),
        "emailAddress"
      );
    }
    return type === "required" ? requiredAttendees : optionalAttendees;
  }

  /**
   * Adds the emails to the recipient list for an appointment or message.
   * If the email already exists, it will not be added again.
   *
   * @param {"required" | "optional"} type
   * @param {string[]} emails - The email addresses the will be set on the event
   */
  public async addAttendees(type: "required" | "optional", emails: string[]): Promise<void> {
    if (!this.item) throw new Error("Item not available");

    if (type !== "required" && type !== "optional") {
      throw new Error("Invalid type. Use 'required' or 'optional'.");
    }

    const currentAttendees = await this.attendees(type);
    const currentEmails = currentAttendees
      ? currentAttendees.map((attendee) => attendee.emailAddress)
      : [];
    const newEmails = uniq(emails.filter((email) => !currentEmails.includes(email)));
    await this._addAttendees(type, newEmails);
  }

  /**
   * Overrides the current attendees with the new list of emails.
   *
   * @param {"required" | "optional"} type
   * @param {string[]} emails - The email addresses the will be set on the event
   */
  public async setAttendees(type: "required" | "optional", emails: string[]): Promise<void> {
    if (!this.item) throw new Error("Item not available");

    if (type !== "required" && type !== "optional") {
      throw new Error("Invalid type. Use 'required' or 'optional'.");
    }

    await this._setAttendees(type, uniq(emails));
  }

  //#endregion

  public async body(
    type: Office.CoercionType | string,
    options: Office.AsyncContextOptions = {}
  ): Promise<string | null> {
    return new Promise((resolve, reject) => {
      if (!this.item) {
        reject("Item not available");
        return;
      }

      this.item.body.getAsync(type, options, (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          resolve(result.value);
        } else {
          logger.error("Error getting body", result.error.message);
          resolve(null);
        }
      });
    });
  }

  public async setBody(
    content: string,
    options: Office.AsyncContextOptions & Office.CoercionTypeOptions
  ): Promise<boolean> {
    return new Promise((resolve, reject) => {
      if (!this.item) {
        reject(false);
        return;
      }

      this.item.body.setAsync(content, options, (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          resolve(true);
        } else {
          logger.error("Error saving body", result.error.message);
          resolve(false);
        }
      });
    });
  }

  public async addCADchatMeetingDetails(spaceId: string, spaceName: string): Promise<boolean> {
    if (!this.item) throw new Error("Item not available");

    /** create the url to our meeting */
    const meetingLink = getCADchatSpaceLink(spaceId);

    /** get the existing body content and remove the */
    const body = await this.getExistingHtmlBody(true);
    if (meetingLink && body?.content?.includes(meetingLink)) return true;

    /** create the full description */
    const description = [
      body?.content || "",
      body?.hasExistingContent ? `<br />` : "",
      `<b>CADchat Meeting Information</b>`,
      `<p>`,
      `You've been invited to join a meeting in the space ${spaceName}.<br /><br />`,
      `Click here to join the meeting: <a title="Join the meeting in CADchat" href="${meetingLink}">${meetingLink}</a>`,
      `</p>`,
      `<p>`,
      `If you don't have a CADchat account, you will be asked to make one to join the call. It's fast and free!`,
      `</p>`,
    ].join("");

    return await this.setBody(description, { coercionType: Office.CoercionType.Html });
  }

  public async hasPendingCADchatMeetingDetails(): Promise<boolean> {
    const body = await this.body(Office.CoercionType.Html);
    if (hasCADchatMeetingStagePayload(body)) return true;

    // On the web the body's data-cadchat-stage attribute is stripped by the sanitizer, so fall back
    // to the custom property to correctly reflect the staged state (e.g. keep the button disabled).
    return await this.hasPendingCADchatMeetingProperty();
  }

  private async hasPendingCADchatMeetingProperty(): Promise<boolean> {
    if (!this.item) return false;

    return new Promise((resolve) => {
      this.item.loadCustomPropertiesAsync((result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded && result.value) {
          resolve(!!result.value.get(CADCHAT_STAGE_CUSTOM_PROPERTY));
        } else {
          resolve(false);
        }
      });
    });
  }

  public async addPendingCADchatMeetingDetails(
    payload: CADchatMeetingStagePayload
  ): Promise<boolean> {
    if (!this.item) throw new Error("Item not available");

    // Persist the payload to the item's custom properties. This is the reliable transport to the
    // on-send handler: unlike the body's data-cadchat-stage attribute (below), custom properties
    // are not stripped by Outlook on the web's body sanitizer.
    await this.setPendingCADchatMeetingProperty(payload);

    const body = await this.getExistingHtmlBody(true);
    const description = addOrReplaceCADchatPendingMeetingBlock(body?.content, payload);

    return await this.setBody(description, { coercionType: Office.CoercionType.Html });
  }

  /**
   * Stores the staging payload in the current item's custom properties so the on-send handler can
   * read it even when the body has been sanitized (e.g. Outlook on the web strips data-* attributes).
   */
  private async setPendingCADchatMeetingProperty(
    payload: CADchatMeetingStagePayload
  ): Promise<void> {
    if (!this.item) return;

    return new Promise((resolve) => {
      this.item.loadCustomPropertiesAsync((loadResult) => {
        if (loadResult.status !== Office.AsyncResultStatus.Succeeded || !loadResult.value) {
          logger.error("Error loading custom properties", loadResult.error?.message);
          resolve();
          return;
        }

        const customProperties = loadResult.value;
        customProperties.set(
          CADCHAT_STAGE_CUSTOM_PROPERTY,
          encodeCADchatMeetingStagePayload(payload)
        );

        customProperties.saveAsync((saveResult) => {
          if (saveResult.status !== Office.AsyncResultStatus.Succeeded) {
            logger.error("Error saving custom properties", saveResult.error?.message);
          }
          resolve();
        });
      });
    });
  }

  /**
   * Un-stages a pending CADchat meeting: removes the custom-property payload and the visible pending
   * block from the body, and clears the location we set during staging. After this the on-send
   * handler finds no payload and won't create a CADchat meeting, letting the user adjust or cancel.
   */
  public async removePendingCADchatMeetingDetails(): Promise<boolean> {
    if (!this.item) throw new Error("Item not available");

    // Remove the custom-property transport so the on-send handler sees no staged meeting.
    const removedProperty = await this.clearPendingCADchatMeetingProperty();

    // Remove the visible pending block from the body (robust across desktop + web).
    const body = await this.getExistingHtmlBody(true);
    const original = body?.content ?? "";
    const cleaned = removeCADchatPendingMeetingBlock(original);
    const bodyChanged = cleaned !== original;

    // Nothing was staged — skip the body/location writes so this is safe to call unconditionally
    // (e.g. from the Back button) without disturbing an event that has no CADchat meeting.
    if (!removedProperty && !bodyChanged) return false;

    if (bodyChanged) {
      await this.setBody(cleaned.trim(), { coercionType: Office.CoercionType.Html });
    }

    // Only clear the location if it's still the value staging set, so we don't wipe a user's own.
    const currentLocation = await this.location();
    if (currentLocation === "CADchat Meeting") {
      await this.setLocation("");
    }

    return true;
  }

  private async clearPendingCADchatMeetingProperty(): Promise<boolean> {
    if (!this.item) return false;

    return new Promise((resolve) => {
      this.item.loadCustomPropertiesAsync((loadResult) => {
        if (loadResult.status !== Office.AsyncResultStatus.Succeeded || !loadResult.value) {
          resolve(false);
          return;
        }

        const customProperties = loadResult.value;

        // Nothing staged in custom properties — don't trigger an unnecessary save.
        if (customProperties.get(CADCHAT_STAGE_CUSTOM_PROPERTY) === undefined) {
          resolve(false);
          return;
        }

        customProperties.remove(CADCHAT_STAGE_CUSTOM_PROPERTY);

        customProperties.saveAsync((saveResult) => {
          if (saveResult.status !== Office.AsyncResultStatus.Succeeded) {
            logger.error("Error clearing custom properties", saveResult.error?.message);
          }
          resolve(true);
        });
      });
    });
  }

  public async getAttachments(): Promise<Office.AttachmentDetailsCompose[]> {
    return new Promise((resolve, reject) => {
      if (!this.item) {
        reject("Item not available");
        return;
      }

      this.item.getAttachmentsAsync((result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          resolve(result.value);
        } else {
          logger.error("Error getting attachments", result.error.message);
          resolve([]);
        }
      });
    });
  }

  public async event(): Promise<CalendarEvent | undefined> {
    if (!this.isReady || !this.item) return undefined;

    try {
      const subject = await this.subject();
      const start = await this.startDate();
      const end = await this.endDate();
      const location = await this.location();
      const attendees = await this.attendees();
      const itemId = await this.itemId();
      const type = this.itemType();
      const id = itemId ? this.getRestId(itemId, Office.MailboxEnums.RestVersion.v2_0) : undefined;

      return {
        id,
        itemId,
        type,
        subject,
        start,
        end,
        location,
        attendees: attendees ? attendees.map((attendee) => attendee.emailAddress) : [],
      };
    } catch (error) {
      logger.error("Error getting event details", error);
      return undefined;
    }
  }

  public async saveEvent(data: EventUpdate): Promise<void> {
    if (!this.item) throw new Error("Item not available");

    if (data.subject) {
      const success = await this.setSubject(data.subject);
      if (!success) throw new Error("Failed to set subject");
    }
    // if (data.location) {
    //   const success = await this.setLocation(data.location);
    //   if (!success) throw new Error("Failed to set location");
    // }
    if (data.attendees) await this.addAttendees("required", data.attendees);
    if (data.body) {
      const success = await this.setBody(data.body.content, data.body.options);
      if (!success) throw new Error("Failed to set body");
    }

    await this._save();
  }

  /**
   *
   * @deprecated
   *
   * @param closeOnSendComplete
   * @returns
   */
  public async sendEvent(closeOnSendComplete = false): Promise<boolean> {
    return new Promise((resolve, reject) => {
      if (!this.item) {
        logger.warn("Item does not exist", this.item);
        reject(false);
        return;
      }

      try {
        this.item.sendAsync((result) => {
          try {
            if (result.status === Office.AsyncResultStatus.Succeeded) {
              /** attempt to close this event */
              if (closeOnSendComplete) this.item.close();
              resolve(true);
              return;
            } else {
              logger.error("Error saving event", result);
              reject(false);
              return;
            }
          } catch (err) {
            logger.error("Internal Error sending event", err);
            reject(false);
          }
        });
      } catch (error) {
        logger.error("Error sending event", error);
        reject(false);
      }
    });
  }

  /**
   *
   * @deprecated
   *
   * @param closeOnSendComplete
   * @returns
   */
  public async closeEvent(): Promise<boolean> {
    return new Promise((resolve, reject) => {
      if (!this.item) {
        reject(false);
        return;
      }

      logger.info("Closing event");
      try {
        this.item.closeAsync((result) => {
          logger.info("Close result:", result);
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            resolve(true);
          } else {
            logger.error(result?.error?.message ?? "Error closing item");
            reject(false);
          }
        });
      } catch (error) {
        logger.error("Error closing item:", error);
        this.item.close();
        resolve(true);
      }
    });
  }

  /**
   * Adds the emails to the recipient list for an appointment or message.
   *
   * @param {"required" | "optional"} type
   * @param {string[]} emails - The email addresses the will be set on the event
   */
  private async _addAttendees(type: "required" | "optional", emails: string[]): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.item) {
        reject("Item not available");
        return;
      }
      if (type !== "required" && type !== "optional") {
        reject("Invalid type. Use 'required' or 'optional'.");
        return;
      }
      if (type === "required") {
        this.item.requiredAttendees.addAsync(emails, (result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            resolve();
          } else {
            logger.error("Error adding required attendees", result.error.message);
            resolve();
          }
        });
      }
      if (type === "optional") {
        this.item.optionalAttendees.addAsync(emails, (result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            resolve();
          } else {
            logger.error("Error adding optional attendees", result.error.message);
            resolve();
          }
        });
      }
    });
  }

  /**
   * Sets a recipient list for an appointment or message.
   *
   * The setAsync method overwrites the current recipient list.
   *
   * @param {"required" | "optional"} type
   * @param {string[]} emails - The email addresses the will be set on the event
   */
  private async _setAttendees(type: "required" | "optional", emails: string[]): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.item) {
        reject("Item not available");
        return;
      }
      if (type !== "required" && type !== "optional") {
        reject("Invalid type. Use 'required' or 'optional'.");
        return;
      }
      if (type === "required") {
        this.item.requiredAttendees.setAsync(emails, (result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            resolve();
          } else {
            logger.error("Error adding required attendees", result.error.message);
            resolve();
          }
        });
      }
      if (type === "optional") {
        this.item.optionalAttendees.setAsync(emails, (result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            resolve();
          } else {
            logger.error("Error adding optional attendees", result.error.message);
            resolve();
          }
        });
      }
    });
  }

  private async _save(): Promise<boolean> {
    return new Promise((resolve) => {
      if (!this.item) {
        resolve(false);
        return;
      }

      this.item.saveAsync((result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          resolve(true);
        } else {
          logger.error("Error saving event", result.error.message);
          resolve(false);
        }
      });
    });
  }

  private async getExistingHtmlBody(
    removeAutoGeneratedMobileSignature = true
  ): Promise<{ content: string; hasExistingContent: boolean }> {
    // get existing body content
    const bodyContent = await this.body(Office.CoercionType.Html);

    if (!removeAutoGeneratedMobileSignature) {
      return { content: bodyContent || "", hasExistingContent: true };
    }

    // Create a DOM parser to manipulate the HTML content
    const parser = new DOMParser();

    // Parse the existing body content
    const doc = parser.parseFromString(bodyContent, "text/html");

    // locate the signature div
    const signatureDiv = doc.getElementById("ms-outlook-mobile-signature");

    // if the signature div exists and contains "Outlook for", remove it
    // This is a workaround to remove the Outlook for Mac signature or possibly other signatures
    // that might be added by Outlook. This shouldn't remove custom user created signatures.
    if (signatureDiv && /outlook for/i.test(signatureDiv.textContent)) {
      signatureDiv.remove();
    }

    // return the cleaned HTML content
    return {
      content: doc.body.innerHTML,
      hasExistingContent: doc.body.textContent.trim().length > 0,
    };
  }
}
